import { hashAction, hashPosition, findBrokerAbb } from "../../component/Methods";
import { formatTickerSymbolDisplay } from "./utility";
import {
  getLastOrder,
  parseDistributionKeyParts,
  resolveDistributionEntities,
  resolveAggregateDistributionLabel,
  computeProRataChildFillMetrics,
  resolveFilledPercentAndState,
  resolveWorkingOrderFillMetrics,
  buildTradeTypeDisplay,
  formatOrderTimestamp,
  findRouteByRouterId,
  isRouterIdSet,
  assignDailySerialNumbers,
} from "./tradeBlotterHelpers";

const NON_ROUTABLE_INVESTMENTS = new Set([8, 9, 11]);

function getMarketPrice(livePrices, symbol) {
  if (!symbol || !Array.isArray(livePrices)) return 0;
  const target = symbol.toLowerCase();
  const match = livePrices.find(
    (p) => p?.symbol && p.symbol.toLowerCase() === target
  );
  return Number(match?.price) || 0;
}

function resolveFillMetrics(tradeObj, lastManual, totalOrderedShares, isBrokerCross) {
  const hasFill = tradeObj.fill && Object.keys(tradeObj.fill).length > 0;
  const hasManual = tradeObj.manuals?.length > 0;

  if (hasManual) {
    const pricePerShare = lastManual.sharePrice || 0;
    const filledShares = lastManual.shareCount || 0;
    const { state, filledPercent } = resolveFilledPercentAndState(
      totalOrderedShares,
      filledShares,
      isBrokerCross
    );
    return { state, filledPercent, pricePerShare, filledShares };
  }
  if (hasFill) {
    const pricePerShare = tradeObj.fill.sharePrice || 0;
    const filledShares = tradeObj.fill.shareCount || 0;
    const { state, filledPercent } = resolveFilledPercentAndState(
      totalOrderedShares,
      filledShares,
      isBrokerCross
    );
    return { state, filledPercent, pricePerShare, filledShares };
  }
  return resolveWorkingOrderFillMetrics(isBrokerCross);
}

function buildChildTrades(
  distributionType,
  lastOrder,
  filledShares,
  totalOrderedShares,
  isBrokerCross,
  context
) {
  if (distributionType !== "P") return [];
  return Object.entries(lastOrder.distribution || {})
    .map(([distributionKey, shareCount]) => {
      const parsed = parseDistributionKeyParts(distributionKey);
      if (!parsed) return null;
      const resolved = resolveDistributionEntities(distributionKey, context);
      const childFill = computeProRataChildFillMetrics(
        filledShares,
        totalOrderedShares,
        shareCount,
        isBrokerCross
      );
      return {
        account: parsed.account,
        fund: resolved.fundName,
        strategy: resolved.strategyName,
        custodian: resolved.custodianName,
        sharesOrdered: shareCount,
        sharesFilled: childFill.filledShares,
        filledPercent: childFill.filledPercent,
        distributionKey,
      };
    })
    .filter(Boolean);
}

function getOptionLabel(lastOrder) {
  const o = lastOrder?.ticker?.option;
  if (o === 0 || o === "0") return "Calls";
  if (o === 1 || o === "1") return "Puts";
  return "";
}

function formatExpirationDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function resolveRouteStatus(filledPercent, orderRouterId) {
  const fp = Number(filledPercent) || 0;
  if (fp >= 100) return "Filled";
  if (fp > 0) return "Partially Filled";
  if (isRouterIdSet(orderRouterId)) return "Routed";
  return "Not Routed";
}

function parseCancellationStatus(tradeObj) {
  const raw = tradeObj?.cancel?.cancellationStatus;
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

export function transformTradeToRow(tradeObj, context) {
  if (!tradeObj) return null;

  const lastOrder = getLastOrder(tradeObj);
  if (!lastOrder) return null;

  const {
    funds,
    strategies,
    custodians,
    brokers,
    routes,
    livePrices,
    onEditClick,
    onManageFillsClick,
  } = context;

  const entityContext = { funds, strategies, custodians };
  const lastManual =
    tradeObj.manuals?.length > 0
      ? tradeObj.manuals[tradeObj.manuals.length - 1]
      : null;

  const distributionKeys = Object.keys(lastOrder.distribution || {});
  const distributionType = distributionKeys.length > 1 ? "P" : "S";
  const resolvedLegs = distributionKeys.map((key) =>
    resolveDistributionEntities(key, entityContext)
  );

  let fundName = "N/A";
  let strategyName = "N/A";
  let custodianName = "N/A";
  let totalOrderedShares = 0;

  if (distributionKeys.length > 0) {
    for (const key of distributionKeys) {
      totalOrderedShares += lastOrder.distribution[key];
    }
    if (distributionType === "P") {
      fundName = resolveAggregateDistributionLabel(
        resolvedLegs.map((leg) => leg.fundName)
      );
      strategyName = resolveAggregateDistributionLabel(
        resolvedLegs.map((leg) => leg.strategyName)
      );
    } else {
      fundName = resolvedLegs[0]?.fundName || "N/A";
      strategyName = resolvedLegs[0]?.strategyName || "N/A";
    }
    custodianName = resolvedLegs[0]?.custodianName || "N/A";
  }

  const isSmaTrade =
    distributionKeys.length > 0 &&
    distributionKeys.every((key) => {
      const p = key.split("#");
      return p.length >= 3 && p[2] === "1";
    });

  const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
  const isBrokerCross = brokerAbb === "cros";
  const { state, filledPercent, pricePerShare, filledShares: rawFilledShares } =
    resolveFillMetrics(tradeObj, lastManual, totalOrderedShares, isBrokerCross);

  // Cros is treated as 100% filled; use ordered qty when fill shareCount is missing/0.
  const filledShares =
    isBrokerCross && !(rawFilledShares > 0)
      ? totalOrderedShares
      : rawFilledShares || 0;

  const displayPricePerShare = isBrokerCross
    ? getMarketPrice(livePrices, lastOrder.ticker?.symbol)
    : pricePerShare;

  const action = hashAction(lastOrder.action);
  const position = hashPosition(lastOrder.position);
  const isOptionTrade = Number(lastOrder.ticker?.investment) === 10;
  const { tradeType, isOpeningPosition } = buildTradeTypeDisplay(
    action,
    position,
    isOptionTrade
  );

  const tickerInvestment = lastOrder.ticker?.investment;
  const orderRouterId = tradeObj.router?.router;
  const routeMatch = findRouteByRouterId(routes, orderRouterId);
  const brokerName = findBrokerAbb(lastOrder.broker, brokers) || "N/A";
  const foundFund = funds.find((f) => f.name === fundName);

  const firstDistributionKey = distributionKeys[0];
  const account = firstDistributionKey
    ? firstDistributionKey.split("#")[0] || ""
    : "";

  return {
    id: tradeObj.tradeId,
    serialNumber: 1,
    timestamp: formatOrderTimestamp(lastOrder?.date),
    ticker:
      (lastOrder.ticker?.symbol && formatTickerSymbolDisplay(lastOrder.ticker)) ||
      "N/A",
    fund: fundName,
    strategy: strategyName,
    state,
    lastOrder,
    side: tradeType,
    routing: lastOrder.routing,
    routable: NON_ROUTABLE_INVESTMENTS.has(tickerInvestment) ? "No" : "Yes",
    routeStatus: resolveRouteStatus(filledPercent, orderRouterId),
    cancellationStatus: parseCancellationStatus(tradeObj),
    routerDisplay: routeMatch?.abbreviation?.toUpperCase() || "",
    options: getOptionLabel(lastOrder),
    strikePrice:
      lastOrder?.ticker?.strikePrice == null ? "" : lastOrder.ticker.strikePrice,
    expirationDate: formatExpirationDate(lastOrder?.ticker?.expirationDate),
    allocation: tradeObj?.allocation ?? null,
    investment: tickerInvestment,
    isOpeningPosition,
    isSmaTrade,
    quantity: totalOrderedShares,
    filledShares,
    orderType: lastOrder.type === 1 ? "Market" : "Limit",
    pricePerShare: displayPricePerShare,
    commPerShare: Number(tradeObj.commission?.commission || 0).toFixed(4),
    custodian: custodianName,
    broker: brokerName,
    filledPercent,
    expandable: true,
    tradeId: tradeObj.tradeId,
    orderDate: lastOrder.date,
    action,
    position,
    totalOrderedShares,
    distributionLegs: resolvedLegs,
    hasProcess: !!lastOrder.process,
    processId: lastOrder.process || null,
    user: lastOrder.user || "asfand@plutus21.com",
    distributionType,
    childTrades: buildChildTrades(
      distributionType,
      lastOrder,
      filledShares,
      totalOrderedShares,
      isBrokerCross,
      entityContext
    ),
    fundAbbreviation: foundFund?.abbreviation || "",
    brokerAbbreviation: brokerName === "N/A" ? "" : brokerName,
    account,
    onEditClick,
    onManageFillsClick,
  };
}

export function transformTradesForGrid(
  dataSource,
  context
) {
  if (!dataSource?.length) return [];

  const rows = dataSource
    .map((tradeObj) => transformTradeToRow(tradeObj, context))
    .filter((row) => row?.orderDate);

  return assignDailySerialNumbers(rows);
}
